import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { Skeleton, message, Tooltip, Select, TreeSelect, Tour, Spin, Slider, InputNumber } from "antd";
import { FaExclamationTriangle } from "react-icons/fa";
import {
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaBroom,
  FaEye,
  FaEyeSlash,
  FaXmark,
} from "react-icons/fa6";
import { MdSettingsSuggest } from "react-icons/md";

import "./styles.scss";
import ChatCard, { ChatLoadingCard } from "./chart-card";
import FileSaver from "../file-saver";
import PlaybookMain from "../playbooks";
import DisclosuresList from "../disclosures";
import CustomModal from "../../../assets/component/modal";
import ChatInput from "../../../assets/component/chat-input";
import PDFPreview from "../../../assets/component/pdf-preview";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import ManageFilters from "../../../assets/component/manage-filters";
import { getParentAndChildrenOptions } from "../../../helpers/array-format";
import {
  FetchCompaniesAction,
  FetchCompaniesReset,
} from "../../../store/slices/ai-chat/fetchCompanies";
import {
  SourceListAction,
  SourceListReset,
} from "../../../store/slices/ai-chat/sourceList";
import { MaxLimitAction } from "../../../store/slices/internal/maxLimit";
import {
  ESGChatAction,
  ESGChatReset,
  ESGChatFeedbackAction,
  ESGChatFeedbackReset,
  ESGChatFoldersAction,
  ESGChatFoldersReset,
  ESGChatControversyAction,
  ESGChatControversyReset,
} from "../../../store/slices/ai-chat/esgChat";
import { findNodeByValue, getAllDescendantNodes } from "../../../helpers/tree-select";
import { scrollToBottom, scrollToNewChat } from "../../../helpers/chat-scrollers";
import { EditModal } from "../../custom-playbook/view-playbook";
import { editPlaybookQuestionsReset } from "../../../store/slices/manage-playbook/editPlaybookQuestions";
import { viewPlaybookReset } from "../../../store/slices/manage-playbook/playbook";
import CustomPlaybookMain from "../playbooks/custom-playbooks";
import { getPromptFiltersData, promptFiltersDataReset } from "../../../store/slices/gemini/prompt-filters";
import { extractVariableAction, extractVariableReset } from "../../../store/slices/gemini/extract-variable";
import { RetrievePortfolioData } from "../../../store/slices/portfolio/retrievePortfolio";
import Portfolio from "../../preferences/portfolio";
import { createPortfolioReset } from "../../../store/slices/portfolio/createPortfolio";
import { decryptData } from "../../../helpers/encrypt/crypto-encrypt";
import { ChunkEvaluationAction, chunkEvaluationReset } from "../../../store/slices/ai-chat/chunk-evaluation";

const defaultOption = { label: "All", title: "All", value: "All" };

message.config({ maxCount: 1, duration: 2 })

const rangeSelectOptions = [
  { label: "Last 1 month", value: "Last 1 month" },
  { label: "Last 3 months", value: "Last 3 months" },
];

const researchTypeOptions = [
  { label: "Company", value: "Company" },
  { label: "Investor", value: "Investor" }
]

export const searchByOptions = [
  {
    key: 1,
    title: "Investor",
    value: "Investor",
    isDisabled: false,
    payloadSearchBy: "Public",
    payloadSearchType: "Investor",
    payloadType: "investor",
    pluralText: "Investors", // For History
    storeName: "investor-filters", // Filters storage
    companyOption: {
      type: "select",
      label: "Investor Name",
      placeholder: "Select Investor",
      isMultiSelect: true,
      isRequired: true,
      length: 10,
      errorMessage: "Please select an Investor",
      maxError: "Investor",
    },
    sourceOption: {
      isRequired: true,
      disclosureAlert: true
    },
    fiscalYearOption: {
      isRequired: true,
      defaultYear: true,
    },
    rangeOption: {
      isRequired: false,
    },
    countryOption: {
      isRequired: true,
    },
    viewPlaybook: {
      isRequired: true,
    },
    researchType: {
      isRequired: true,
    }
  },
  {
    key: 2,
    title: "Company",
    value: "Company",
    isDisabled: false,
    payloadSearchBy: "Public",
    payloadSearchType: "Company",
    payloadType: "company",
    pluralText: "Companies", // For History
    storeName: "company-filters",
    companyOption: {
      type: "select",
      label: "Portfolio Companies",
      placeholder: "Select Company",
      isMultiSelect: true,
      isRequired: true,
      length: 5,
      errorMessage: "Please select a Company",
      maxError: "Company",
    },
    fiscalYearOption: {
      isRequired: true,
    },
    rangeOption: {
      isRequired: false,
    },
    sourceOption: {
      isRequired: true,
      disclosureAlert: true
    },
    countryOption: {
      isRequired: true,
    },
    viewPlaybook: {
      isRequired: true,
    },
    documentLanguage: {
      isRequired: true,
    },
    researchType: {
      isRequired: true,
    }
  },
  {
    key: 3,
    title: "Custom",
    value: "Custom",
    isDisabled: false,
    payloadSearchBy: "Private",
    payloadSearchType: "Custom",
    payloadType: "custom",
    pluralText: "Folders", // For History
    storeName: "custom-filters",
    companyOption: {
      type: "select",
      label: "Folder Name",
      placeholder: "Select Folder",
      isMultiSelect: false,
      isRequired: true,
      errorMessage: "Please select a Folder",
      maxError: "Folder",
    },
    sourceOption: {
      isRequired: false,
      disclosureAlert: true
    },
    fiscalYearOption: {
      isRequired: false,
    },
    rangeOption: {
      isRequired: false,
    },
    countryOption: {
      isRequired: false,
    },
    viewPlaybook: {
      isRequired: true,
    },
  },
  {
    key: 4,
    // title: "Folder/File",
    title: "Custom Internal",
    value: "Custom-Internal",
    isDisabled: false,
    payloadSearchBy: "Private",
    payloadSearchType: "Custom",
    payloadType: "custom-internal",
    pluralText: "Folders", // For History
    storeName: "custom-internal-filters",
    companyOption: {
      type: "tree-select",
      label: "Folder Names",
      placeholder: "Select Folders",
      isMultiSelect: false,
      isRequired: true,
      errorMessage: "Please select a Folder",
      maxError: "Folder",
    },
    sourceOption: {
      isRequired: false,
      disclosureAlert: true
    },
    fiscalYearOption: {
      isRequired: false,
    },
    rangeOption: {
      isRequired: false,
    },
    countryOption: {
      isRequired: false,
    },
    viewPlaybook: {
      isRequired: true,
    },
  },
  {
    key: 5,
    title: "Real Estate",
    value: "RealEstate",
    payloadSearchBy: "Public",
    payloadSearchType: "RealEstate",
    payloadType: "investor",
    pluralText: "Investors", // For History
    storeName: "real-estate-filters",
    companyOption: {
      type: "select",
      label: "Real Estate Investor",
      placeholder: "Select Investor",
      isMultiSelect: true,
      isRequired: true,
      length: 5,
      errorMessage: "Please select an Investor",
      maxError: "Investor",
    },
    sourceOption: {
      isRequired: true,
      disclosureAlert: true
    },
    fiscalYearOption: {
      isRequired: true,
    },
    rangeOption: {
      isRequired: false,
    },
    countryOption: {
      isRequired: true,
    },
    viewPlaybook: {
      isRequired: true,
    },
  },
  {
    key: 6,
    title: "Contro Research",
    value: "Contro",
    payloadSearchBy: "Public",
    payloadSearchType: "Company",
    payloadType: "contro",
    pluralText: "Companies", // For History
    storeName: "contro-research-filters",
    companyOption: {
      type: "select",
      label: "Company Name",
      placeholder: "Select Company",
      isMultiSelect: true,
      isRequired: true,
      length: 5,
      errorMessage: "Please select a Company",
      maxError: "Company",
    },
    sourceOption: {
      isRequired: false,
      disclosureAlert: true
    },
    fiscalYearOption: {
      isRequired: false,
    },
    rangeOption: {
      isRequired: true,
    },
    countryOption: {
      isRequired: true,
    },
    viewPlaybook: {
      isRequired: false,
    },
  },
  {
    key: 7,
    title: "Fund",
    value: "Fund",
    isDisabled: false,
    payloadSearchBy: "Public",
    payloadSearchType: "Fund",
    payloadType: "fund",
    pluralText: "Funds", // For History
    storeName: "fund-filters",
    companyOption: {
      type: "select",
      label: "Fund Name",
      placeholder: "Select Fund",
      isMultiSelect: true,
      isRequired: true,
      length: 5,
      errorMessage: "Please select a Fund",
      maxError: "Funds",
    },
    fiscalYearOption: {
      isRequired: false,
      defaultYear: ["FY-2025"]
    },
    rangeOption: {
      isRequired: false,
    },
    sourceOption: {
      isRequired: true,
      disclosureAlert: false
    },
    countryOption: {
      isRequired: true,
    },
    viewPlaybook: {
      isRequired: true,
    },
  },
  {
    key: 8,
    title: "Modify Prompt",
    value: "Modify-Prompt",
    isDisabled: false,
    payloadSearchBy: "Gemini",
    payloadSearchType: "Private",
    payloadType: "modifyPrompt",
    pluralText: "Investors", // For History
    storeName: "investor-filters", // Filters storage
    companyOption: {
      type: "select",
      label: "Entity Name",
      placeholder: "Select Entity",
      isMultiSelect: true,
      isRequired: true,
      length: 1,
      errorMessage: "Please select an Entity",
      maxError: "Entity",
    },
    sourceOption: {
      isRequired: false,
      disclosureAlert: false
    },
    fiscalYearOption: {
      isRequired: true,
    },
    rangeOption: {
      isRequired: false,
    },
    childVariable: {
      isRequired: false,
    },
    countryOption: {
      isRequired: false,
    },
    viewPlaybook: {
      isRequired: true,
    },
  },
];

export const filterFolders = (folders) => {
  return folders
    .map((folder) => {
      const children = folder.children ? filterFolders(folder.children) : [];
      if (children.length > 0 || !folder?.isLeaf) {
        return {
          ...folder,
          children,
        };
      }
      return null;
    })
    .filter(Boolean);
};


// Format New Chat
export const formatChatReference = ({ data, searchType }) => {
  const textToSplit = ["Sirius/", "Cygnus/"];
  const customLink = data?.location?.s3Location?.uri || "";
  let customSourceName = customLink;

  textToSplit.forEach((separator) => {
    if (customLink?.includes(separator)) {
      customSourceName = customLink.split(separator).pop();
    }
  });

  return {
    date: data?.metadata?.date || "",
    text: data?.content?.text,
    pageNo: data?.metadata?.["x-amz-bedrock-kb-document-page-number"] || "",
    pdfLink: ["Custom", "Custom-Comparison"]?.includes(searchType) ? customLink : data?.metadata?.sourceFile,
    sourceName:
      ["Custom", "Custom-Comparison"]?.includes(searchType) ? customSourceName : data?.metadata?.sourceTitle,
    sourceURI: data?.metadata?.sourceURI || "",
    sourceURILink: data?.metadata?.url || "",
    publicationDate: data?.metadata?.publicationDate
      ? dayjs(data?.metadata?.publicationDate).format("DD-MMM-YYYY")
      : "",
    companyName: data?.metadata?.companyName || data?.metadata?.entityName,
    searchType: searchType,
    sourceTypeName: data?.metadata?.sourceTypeName || "",
    sourceSubTypeName: data?.metadata?.sourceSubTypeName || "",
  };
};

export const formatChunkReference = (data) => {
  if (!data) return {};

  return {
    date: data?.publicationDate || "",
    text: data?.text_chunk || "",
    pageNo: data?.pageNumber || "",
    pdfLink: data?.sourceUrl || "",
    sourceName: data?.sourceTitle || "",
    publicationDate: data?.publicationDate
      ? dayjs(data?.publicationDate).format("DD-MMM-YYYY")
      : "",
    companyName: data?.entityName || "",
    sourceTypeName: data?.sourceTypeName || "",
    sourceSubTypeName: data?.sourceSubTypeName || "",
    fiscalYear: data?.fiscalYear || "",
    score: data?.score || "",
    vs_score: (data?.vs_score === null) ? "-" : data?.vs_score,
    fts_score: data?.fts_score || ""
  };
};

// Format Company List
const FormattedDropList = ({ contents, closeDropList, setValues }) => {
  const filterItem = (value) => {
    if (contents?.length > 0) {
      const filteredContents = contents?.filter((item) => item?.value !== value);
      setValues(filteredContents?.map((item) => item?.value) || []);
    }
  };

  return (
    <>
      {contents?.length > 0 && (
        <div className="selected-contents-wrapper">
          <FaChevronUp
            className="close-section-icon"
            onClick={() => closeDropList()}
          />
          {contents?.map((item) => {
            return (
              <div className="content-wrapper" key={item?.value}>
                {item?.label}
                <FaXmark
                  className="remove-icon"
                  onClick={() => filterItem(item?.value)}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const ESGChatMain = ({
  searchType,
  disableFilters,
  showInputUpload,
  showUploadSection,
  showDisclosuresSection,
  blockCall,
  breadcrumbs,
  promptEvaluation = false,
  promptMode,
  chunkEvaluation
}) => {
  const [questionInput, setQuestionInput] = useState("");
  const [presetQuestion, setPresetQuestion] = useState("");
  const [questionValue, setQuestionValue] = useState("");
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [selectedYear, setSelectedYear] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedSourceDetails, setSelectedSourceDetails] = useState({
    parents: [],
    children: [],
  });
  const [selectedPromptMode, setSelectedPromptMode] = useState("public");
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(defaultOption?.label);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [chatCompanyList, setChatCompanyList] = useState([]);
  const [chatFiscalYearList, setChatFiscalYearList] = useState([]);
  const [chatFoldersList, setChatFoldersList] = useState([]);
  const [countriesOption, setCountriesOption] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [variableOptions, setVariableOptions] = useState([]);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [chatSessionId, setChatSessionId] = useState("");
  const [showAddonSection, setShowAddonSection] = useState(true);
  const [editChunkModal, setEditChunkModal] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState({
    show: false,
    isPdf: true,
  });
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [editPromptModal, setEditPromptModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showDisclosureAlerts, setShowDisclosureAlerts] = useState(false);
  const [showCompaniesList, setShowCompaniesList] = useState(false);
  const [unsyncedFilesCount, setUnsyncedFilesCount] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [promptEvaluationFlag, setPromptEvaluationFlag] = useState(false);
  const [playbookId, setPlaybookId] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [selectedFiltersData, setSelectedFiltersData] = useState(null);
  const [selectedReseachType, setSelectedResearchType] = useState("Company")
  const [selectedModel, setSelectedModel] = useState("Advanced")
  const [searchCompany, setSearchCompany] = useState("");
  const [dropdownDataResetFlag, setDropdownDataResetFlag] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [selectedChunkSize, setSelectedChunkSize] = useState(10);
  const [sliderValue, setSliderValue] = useState({
    vectorWeight: "0.8",
    keywordWeight: "0.2"
  })

  const scrollRef = useRef();
  const debounceTimeout = useRef(null);
  const selectRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dataTypeList = ["Text", "Number", "Select", "MultiSelect"];

  const { pendingTours: pendingTourItemsFromStore } = useSelector((state) => state.tourList?.pendingTourItems);
  const { data: chatResponse, isLoading: loadingStatus, error: chatResponseError } = useSelector((state) => state.esgChat);
  const { data: controversyChatResponse, isLoading: controversyChatLoading, error: controversyChatError } = useSelector((state) => state.esgChatControversy);
  const chatFeedback = useSelector((state) => state.chatFeedback);
  const { data: chatFolders } = useSelector((state) => state.chatFolders);
  const {
    data: editPlaybookQuestionsData,
    isLoading: editPlaybookQuestionsLoading,
    success: editPlaybookQuestionsSuccess,
  } = useSelector((state) => state?.editPlaybookQuestions);
  const {
    entities: companyList,
    countries: countriesList,
    fiscalYear: fiscalYearList,
    publicationYear: publicationYearList,
  } = useSelector((state) => state.fetchCompanies.data);

  const round2 = (num) => Number(num.toFixed(2));

  const { isLoading: companyLoading } = useSelector((state) => state.fetchCompanies)

  const { isLoading: countryLoading } = useSelector((state) => state.fetchCompanies);
  const { error: fetchCompanyError } = useSelector(
    (state) => state.fetchCompanies
  );
  const { data: disclosuresData, isLoading: disclosureLoading } = useSelector((state) => state.sourceList);
  const { data: uploadFolders } = useSelector((state) => state.fetchFolders);
  const { data: promptFiltersData, success: promptFiltersDataSuccess, isLoading: promptFiltersDataLoading, error: promptFiltersDataError } = useSelector((state) => state.promptFilters);
  const { data: extractVariableData, success: extractVariableSuccess, error: extractVariableError, isLoading: extractVariableLoading } = useSelector((state) => state.extractVariable);
  const { data: userPortfolioData } = useSelector((state) => state?.retrievePortfolio) || {};
  const { success: portfolioCreateSuccess } = useSelector((state) => state?.createPortfolio) || {};
  let role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'))
  const isPromptAdmin = role_decrypt?.role === "prompt_admin"
  const isAdmin = role_decrypt?.role === "admin"
  const { data: chunkData, isLoading: chunkLoading, success: chunkSuccess, error: chunkError } = useSelector((state) => state?.chunkEvaluation) || {};

  const promptModeOptions = [
    { label: "Public", value: "public" },
    { label: "Private", value: "private" }
  ]

  const modelOptions = [
    { label: "Advanced", value: "sonnet" },
    { label: "Basic", value: "haiku" },
  ]

  const chunkOptions = [
    { label: 10, value: 10 },
    { label: 15, value: 15 },
    { label: 20, value: 20 },
  ]

  const isPublicationYear = (selectedFiltersData?.title === "Company" && !promptEvaluationFlag && !chunkEvaluation) || selectedFiltersData?.title === "Investor";

  const maxCompanyAllowed = promptEvaluation ? 10 : selectedFiltersData?.companyOption?.length ?? 0;

  const languageOptions = [
    { label: "English", value: "en" },
    { label: "Non-English", value: "non en" },
  ]

  const requestStoredPayload = () => {
    const retrievedData = sessionStorage.getItem("filter-payload");
    const parsedData = retrievedData ? JSON.parse(retrievedData) : null;

    return parsedData;
  };

  const requestCurrentFilters = () => {
    const retrievedData = requestStoredPayload();
    const filteredData =
      retrievedData &&
      (retrievedData?.find(
        (item) => item.searchType === selectedFiltersData?.payloadSearchType
      ) ||
        {});

    return filteredData;
  };

  const storePayload = ({ key, value }) => {
    if (!promptEvaluationFlag && !chunkEvaluation) {
      const retrievedPayload = requestStoredPayload();

      // Filter out the current payload if it exists
      const otherFilters =
        retrievedPayload?.filter(
          (item) => item.searchType !== selectedFiltersData?.payloadSearchType
        ) || [];

      // Find the current payload if it exists
      const existingPayload =
        retrievedPayload?.find(
          (item) => item.searchType === selectedFiltersData?.payloadSearchType
        ) || {};

      const payload = {
        ...existingPayload,
        searchBy: selectedFiltersData?.payloadSearchBy,
        searchType: promptEvaluationFlag ? selectedReseachType : selectedFiltersData?.payloadSearchType,
        [key]: value,
      };

      const formattedData = [...otherFilters, payload];
      sessionStorage.setItem("filter-payload", JSON.stringify(formattedData));
    }
  };

  const handleFeedback = (e) => {
    setFeedback(e);
    const feedId = chunkEvaluation ? chunkData?.feedbackId : chatResponse?.result?.feedbackId;
    const payload = {
      userFeedback: e,
    };

    dispatch(ESGChatFeedbackAction({ payload: payload, id: feedId }));
  };

  const resetFilters = () => {
    setSelectedFolders([]);
    setSelectedSource([]);
    setSelectedSourceDetails([]);
    setSelectedYear([]);
    setSelectedRange(null);
    setSelectedCompany([]);
    setSelectedLanguage(null);
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e);
    setSelectedSource([]);
    setSearchCompany("");
    setCompanyOptions([]);
    storePayload({ key: "companyName", value: [] });
    storePayload({ key: "fiscalYear", value: [] });
    storePayload({ key: "publicationYear", value: [] });
    storePayload({ key: "sourceType", value: [] });
    storePayload({ key: "subSourceType", value: [] });
    storePayload({
      key: "country",
      value: e !== "All" ? e : "",
    });

    resetFilters();
  };

  const handlePromptModeChange = (e) => {
    setSelectedPromptMode(e);
    setSelectedCompany([]);
    setSelectedYear([]);
    setSelectedVariable(null);
    setQuestionInput("");
    setCompanyOptions([]);
    dispatch(FetchCompaniesReset());
    dispatch(promptFiltersDataReset());

    if (e === "private") {
      setSelectedFiltersData(searchByOptions?.find((item) => item?.value === "Modify-Prompt"));
    } else {
      setSelectedFiltersData(searchByOptions?.find((item) => item?.value === searchType));
    }
  }

  const handleCompanyChange = (e) => {
    setSearchCompany("");
    setShowCompaniesList(true);
    setSelectedRange(null);
    setSelectedSource([]);
    storePayload({ key: "fiscalYear", value: [] });
    storePayload({ key: "sourceType", value: [] });
    storePayload({ key: "publicationYear", value: [] });
    storePayload({ key: "subSourceType", value: [] });
    if (selectedPromptMode !== "private") setSelectedYear([]);

    if (chunkEvaluation || promptMode) {
      const selected = chatCompanyList?.find((option) => option.value === e);
      setSelectedCompany(selected ? [selected] : []);
      storePayload({ key: "companyName", value: selected ? [selected] : [] });
      return;
    }

    if (!selectedFiltersData.companyOption?.isMultiSelect) {
      const selected = chatCompanyList?.find((option) => option.value === e);
      setSelectedCompany(selected);
      storePayload({ key: "companyName", value: selected });
      return;
    }

    if (selectedFiltersData.companyOption?.isMultiSelect && e?.length <= maxCompanyAllowed) {
      const isAllSelected = e === "All";
      const prevSelectedCount = selectedCompany?.length || 0;

      if (isAllSelected) {
        storePayload({ key: "companyName", value: defaultOption });
        setSelectedCompany([defaultOption]);
      } else {
        const selected = chatCompanyList.filter((option) => e?.includes(option.value));
        const includesAll = selected.some((option) => option.value === "All");
        if (prevSelectedCount === 1 && selected?.length === 2 && searchType === "Company" && !promptEvaluationFlag && !chunkEvaluation) {
          message.warning("Multiple years are allowed only for a single company selection")
        }
        if (includesAll) {
          storePayload({ key: "companyName", value: defaultOption });
          setSelectedCompany([defaultOption]);
        } else {
          setSelectedCompany(selected);
          storePayload({ key: "companyName", value: selected });
        }
      }
    } else {
      if (selectedFiltersData.companyOption?.isMultiSelect) {
        message.error(
          `Maximum limit to select ${selectedFiltersData?.companyOption?.maxError} is ${maxCompanyAllowed}`
        );
      }
    }
  };

  const handleCompanySearch = (value) => {
    if (searchType === "Investor" || selectedReseachType === "Investor") return;
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    if (value?.length > 2 && selectedPromptMode !== "private") {
      debounceTimeout.current = setTimeout(() => {
        setSearchCompany(value);
      }, 500);
    }
  }

  const handleYearChange = (e) => {
    setSelectedYear(e);
    setSelectedSource([]);
    storePayload({ key: "sourceType", value: [] });
    storePayload({ key: "subSourceType", value: [] });
    if (selectedFiltersData?.fiscalYearOption?.defaultYear) {
      storePayload({
        key: "year",
        value: e !== "All" ? e : [],
      });
    } else {
      storePayload({ key: isPublicationYear ? "publicationYear" : "fiscalYear", value: e });
    }
  };

  const handleRangeChange = (e) => {
    setSelectedRange(e);
    storePayload({ key: "range", value: e });
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e);
  }

  const handleModelChange = (e) => {
    setSelectedModel(e);
  }

  const handleChunkSizeChange = (e) => {
    setSelectedChunkSize(e);
  }

  const handleResearchTypeChange = (e) => {
    setSelectedResearchType(e)
    setSelectedCountry(defaultOption?.label);
    setCompanyOptions([]);
    setSearchCompany("");
    resetFilters();
  }

  const handleVariableChange = (e) => {
    setSelectedVariable(e);
  }

  const format = (v) => {
    const num = Number(v);
    return Math.round(num * 100) / 100;
  };

  const handleVectorWeightChange = (value) => {
    const formatted = format(value);
    const complement = format(1 - formatted);

    setSliderValue({
      vectorWeight: formatted,
      keywordWeight: complement,
    });

    if (selectedQuestion) {
      setSelectedQuestion(prev => ({
        ...prev,
        selectedRow: {
          ...prev.selectedRow,
          vts: formatted,
          fts: complement
        }
      }));
    }
  };

  const handleKeywordWeightChange = (value) => {
    const formatted = format(value);
    const complement = format(1 - formatted);

    setSliderValue({
      keywordWeight: formatted,
      vectorWeight: complement,
    });

    if (selectedQuestion) {
      setSelectedQuestion(prev => ({
        ...prev,
        selectedRow: {
          ...prev.selectedRow,
          fts: formatted,
          vts: complement
        }
      }));
    }
  };

  const handleFolderSelectChange = (selectedValues, _, currentItem) => {
    const maxFoldersToSelect = 5;
    const hasAllSelected = selectedValues.some((item) => item.value === "All");

    if (hasAllSelected) {
      setSelectedFolders([{ label: "All", value: "All" }]);
    } else if ((selectedValues.length > maxFoldersToSelect) && currentItem?.checked) {
      message.error(`Maximum limit to select is ${maxFoldersToSelect}`);
    } else {
      const newSelection = selectedValues.find(
        (item) => !selectedFolders.some((folder) => folder.value === item.value)
      );

      if (newSelection) {
        const selectedNode = findNodeByValue(
          newSelection.value,
          chatFoldersList
        );
        let nodesToAdd = [
          { label: selectedNode.title, value: selectedNode.value },
        ];

        if (selectedNode.children?.length) {
          nodesToAdd = [...nodesToAdd, ...getAllDescendantNodes(selectedNode)];
        }

        const allSelectedNodes = [...selectedFolders, ...nodesToAdd];
        const uniqueSelectedNodes = Array.from(
          new Map(allSelectedNodes.map((item) => [item.value, item])).values()
        );

        setSelectedFolders(uniqueSelectedNodes);
      } else {
        const missingFolders = selectedFolders.filter(
          (folder) =>
            !selectedValues.some((selected) => selected.value === folder.value)
        );

        if (missingFolders.length) {
          const missingFolderValue = missingFolders[0].value;

          const updatedSelectedValues = selectedValues.filter(
            (selected) => !selected.value.includes(missingFolderValue)
          );

          setSelectedFolders(updatedSelectedValues);
        } else {
          setSelectedFolders(selectedValues);
        }
      }
    }
  };

  const handleChangeSource = (values) => {
    if (values?.at(-1) === defaultOption?.value) {
      setSelectedSource([defaultOption?.value]);
      storePayload({ key: "sourceType", value: [] });
      storePayload({ key: "subSourceType", value: [] });
      setSelectedSourceDetails({ parents: [], children: [] });
    } else if (values?.length > 0 && values?.length <= 5) {
      if (values?.includes(defaultOption?.value)) {
        values = values.filter((item) => item !== defaultOption?.value);
      }

      const sourceParentChildDetails = getParentAndChildrenOptions({
        data: disclosuresData?.researchOutput,
        selectedSource: values,
      });

      storePayload({
        key: "sourceType",
        value: sourceParentChildDetails?.parents,
      });
      storePayload({
        key: "subSourceType",
        value: sourceParentChildDetails?.children,
      });

      setSelectedSourceDetails(sourceParentChildDetails);
      setSelectedSource(values);
    } else if (values?.length > 5) {
      message.error("Maximum limit exceeded!");
    } else {
      setSelectedSource([]);
      setSelectedSourceDetails({ parents: [], children: [] });
      storePayload({ key: "sourceType", value: [] });
      storePayload({ key: "subSourceType", value: [] });
    }
  };

  const handleSubmit = (customQuestion = null) => {
    const questionToSubmit = customQuestion || questionInput;
    const isExternalCall = ["contro"]?.includes(selectedFiltersData.payloadType);

    if (!blockCall) {
      const isCustomModel = ["Custom", "Custom-Internal"]?.includes(selectedFiltersData?.value);

      const isCompanyAvailable = (!selectedFiltersData.companyOption?.isMultiSelect ? selectedCompany?.value : selectedCompany?.length > 0) || selectedFolders?.length > 0;
      const isFiscalYearAvailable = (selectedFiltersData?.fiscalYearOption?.isRequired && !selectedFiltersData?.fiscalYearOption?.defaultYear) ? selectedYear?.length > 0 : true;
      const isRangeAvailable = selectedFiltersData?.rangeOption?.isRequired ? selectedRange : true;
      const isCustomFolderAvailable = !isCustomModel ? true : selectedFiltersData?.companyOption?.type === "tree-select" ? selectedFolders?.length > 0 : selectedCompany?.value;
      const payloadYear = (selectedYear === "All" || selectedYear?.length === 0 || !selectedYear) ? [] : selectedYear;
      const payloadFyYear = selectedFiltersData?.title === "Company" || selectedFiltersData?.title === "Investor"

      if (!showLoader && questionToSubmit && (isCustomModel ? isCustomFolderAvailable : (isCompanyAvailable && isFiscalYearAvailable)) && isRangeAvailable
      ) {
        setQuestionValue(questionToSubmit);
        let payload = {
          question: questionToSubmit,
          presetQuestion: presetQuestion,
          year: payloadYear && Array.isArray(payloadYear) ? payloadYear : [payloadYear],
          searchType: (promptEvaluationFlag) ? selectedReseachType : selectedFiltersData?.payloadSearchType,
          searchBy: selectedFiltersData?.payloadSearchBy,
          sessionId: chatSessionId,
          ...(payloadFyYear && { isFyYear: !isPublicationYear })
        };

        if (selectedFiltersData.payloadType === "custom") {
          payload = {
            ...payload,
            customFolder:
              selectedCompany?.value === "All" ? "" : selectedCompany?.value,
            companyName: [],
            isUpload: showUploadSection,
          };
        } else if (selectedFiltersData.payloadType === "custom-internal") {
          const formattedFiles = [];
          const formattedFolders = [];
          const isTreeAllSelected = selectedFolders[0]?.value?.toLowerCase() === "all"

          selectedFolders?.forEach((item) => {
            formattedFolders.push(item?.value);
          });

          payload = {
            ...payload,
            customFolder: "",
            companyName: [],
            isUpload: showUploadSection,
            fileArray: isTreeAllSelected ? [] : formattedFiles,
            folderArray: isTreeAllSelected ? [] : formattedFolders,
          };
        } else if (selectedFiltersData.payloadType === "contro") {
          dispatch(ESGChatControversyReset());
          const companyName = selectedFiltersData?.companyOption?.isMultiSelect
            ? selectedCompany[0]?.value === "All"
              ? []
              : selectedCompany?.map((item) => item?.value)
            : selectedCompany?.value === "All"
              ? ""
              : selectedCompany?.value;

          payload = {
            searchBy: "Contro",
            question: questionInput,
            year: [],
            companyName,
          };

          dispatch(ESGChatControversyAction(payload));
        } else if (promptEvaluationFlag) {
          if (selectedQuestion?.selectedRow?.dataType === "") {
            message.error("Data Type Cannot be Empty!")
            return;
          }
          // Gemini Prompt Modification submit block...
          if (selectedFiltersData?.value && selectedFiltersData?.value === "Modify-Prompt") {
            const payload = {
              entityId: selectedCompany?.map(item => item?.value),
              question: questionInput,
              playbookQuestionId: selectedQuestion?.selectedRow?._id,
              year: selectedYear
            }
            dispatch(extractVariableReset());
            dispatch(extractVariableAction(payload));
          } else {

            const companyName = selectedFiltersData?.companyOption?.isMultiSelect ? selectedCompany[0]?.value === "All" ? [] : selectedCompany?.map((item) => item?.value) : selectedCompany?.value === "All" ? "" : selectedCompany?.value;
            payload = {
              ...payload,
              customFolder: "",
              companyName,
              sourceType: selectedSourceDetails?.parents,
              subSourceType: selectedSourceDetails?.children,
              country:
                selectedCountry === "All" ? [] : [selectedCountry],
              disclosureDetails: disclosuresData?.rightPane || [],
              isPlaybookQuestion: true,
              isLabs: true,
              documentLanguage: selectedLanguage,
              playbookQuestionId: selectedQuestion?.selectedRow?._id,
              dataType: selectedQuestion?.selectedRow?.dataType,
              finalUnits: selectedQuestion?.selectedRow?.finalUnits,
              answeringModel: selectedModel,
              criteria: selectedQuestion?.selectedRow?.criteria,
              inclusion: selectedQuestion?.selectedRow?.inclusion,
              searchQuery: selectedQuestion?.selectedRow?.searchQuery || "",
              fts: selectedQuestion?.selectedRow?.fts || "",
              vts: selectedQuestion?.selectedRow?.vts || ""
            };
          }
        } else if (chunkEvaluation) {
          const payload = {
            searchQuery: questionInput,
            entityId: selectedCompany?.map(item => item?.value),
            year: [selectedYear],
            sourceType: selectedSourceDetails?.parents,
            subSourceType: selectedSourceDetails?.children,
            isLabs: true,
            documentLanguage: selectedLanguage,
            playbookQuestionId: selectedQuestion?.selectedRow?._id,
            vts: sliderValue?.vectorWeight,
            fts: sliderValue?.keywordWeight,
            chunkSize: selectedChunkSize,
            searchType: "Company"
          }
          dispatch(chunkEvaluationReset())
          dispatch(ChunkEvaluationAction(payload))
        } else {
          const companyName = selectedFiltersData?.companyOption?.isMultiSelect ? selectedCompany[0]?.value === "All" ? [] : selectedCompany?.map((item) => item?.value) : selectedCompany?.value === "All" ? "" : selectedCompany?.value;
          payload = {
            ...payload,
            customFolder: "",
            companyName,
            sourceType: selectedSourceDetails?.parents,
            subSourceType: selectedSourceDetails?.children,
            country:
              selectedCountry === "All" ? [] : [selectedCountry],
            disclosureDetails: disclosuresData?.rightPane || [],
          };
        }

        if (!isExternalCall && selectedFiltersData?.value !== "Modify-Prompt" && !chunkEvaluation) {
          // Reset
          dispatch(ESGChatReset());

          // Request
          dispatch(ESGChatAction(payload));
        }

        setQuestionInput("");
        setFeedback("");
      } else {
        if (!isCompanyAvailable) {
          message.error(selectedFiltersData?.companyOption?.errorMessage);
        } else if (
          selectedFiltersData?.fiscalYearOption?.isRequired &&
          selectedYear?.length === 0
        ) {
          promptEvaluationFlag ? message.error("Please select a fiscal year") : message.error("Please select a publication year");
        } else if (!questionInput) {
          message.error("Please add a question");
        } else if (!isCustomFolderAvailable) {
          message.error(selectedFiltersData?.companyOption?.errorMessage);
        } else if (!isRangeAvailable) {
          message.error("Please select Range");
        }
      }
    }
  };

  const handleClearChat = () => {
    setChatData([]);
  };

  const handleExpandCollapse = () => {
    setShowAddonSection(!showAddonSection);
  };

  const handlePDFPreview = ({ url }) => {
    setShowPDFPreview({ show: true, isPdf: url?.includes(".pdf") });
  };

  const handleShowPromptsModal = () => {
    setShowPlaybookModal(!showPlaybookModal);
  };

  const handleShowEditModal = () => {
    setEditPromptModal(!editPromptModal)
    setEditModal(true);
  }

  const handleShowUpdateModal = () => {
    setEditPromptModal(!editPromptModal)
    setEditModal(false)
  }

  const responseFormatter = ({ res, excelReport }) => {
    const currentFilters = requestCurrentFilters();
    const oldData = chatData;    
    
    // Storing Current Chat Session Id
    if (!currentFilters?.sessionId) {
      storePayload({ key: "sessionId", value: res?.sessionId });
      setChatSessionId(res?.sessionId);
    } else {
      setChatSessionId(currentFilters?.sessionId);
    }

    // Formatting response
    if (res) {
      setShowFeedback(true);
      setTimeout(() => {
        scrollToNewChat({ scrollRef: scrollRef });
      }, 500);

      let currentReferences = [];
      let chunkReference = [];

      // Refactor Highlight Data
      if (!promptMode) {        
        currentReferences = ((promptEvaluation && res?.length === 1) ? res[0]?.citations : res?.citations)?.map((item) => {          
           const listData = item?.retrievedReferences?.map((data) => {
             return formatChatReference({
               data: data,
               searchType: selectedFiltersData?.payloadSearchType,
             });
           });
           return listData;
         })
         .flat() || [];
 
        chunkReference = chunkEvaluation ? res?.map((item) => {
         return formatChunkReference(item)
       }).flat() : [];
      }

      const updatedRes = {
        ...(!chunkEvaluation ? (Array.isArray(res) ? { response: res } : { ...res }) : {}),
        question: questionValue,
        sources: promptMode ? [] : chunkEvaluation ? chunkReference : currentReferences,
        ...((promptEvaluationFlag || chunkEvaluation) && {
          dpDetails: {
            ...selectedQuestion?.selectedRow,
          },
          excelReport: excelReport || {}
        })
      };

      const newChatData = [...oldData, updatedRes];
      setChatData(newChatData);
      scrollToNewChat({ scrollRef: scrollRef });
    }
  };

  const handleLoader = ({ status }) => {
    setShowLoader(status);

    if (status) {
      scrollToBottom({ scrollRef: scrollRef });
    }
  };

  const handleResize = () => {
    if (window.innerWidth <= 768) {
      setShowAddonSection(false);
    }
    if (window.innerWidth > 1024) {
      setShowFilters(true);
    }
  };

  const handleShowFiltersSection = () => {
    setShowFilters(!showFilters);
  };

  const handleCloseDisclosureAlert = () => {
    setShowDisclosureAlerts(false);
  };

  const handleShowSelectedItems = () => {
    setShowCompaniesList(!showCompaniesList);
  };

  const handleAddCompanies = () => {
    selectRef.current?.blur();
    setShowPortfolioModal(true);
  }

  // Initial Reset
  useEffect(() => {
    dispatch(ESGChatFoldersReset());
    dispatch(ESGChatReset());
    dispatch(FetchCompaniesReset());
    dispatch(chunkEvaluationReset());
    dispatch(extractVariableReset());
    storePayload({ key: "sessionId", value: "" });
    setChatData([]);
    setChatSessionId("");
    if (promptEvaluation) {
      setPromptEvaluationFlag(true)
      setSelectedModel("haiku");
      dispatch(viewPlaybookReset())
    }
  }, []);

  useEffect(() => {
    if (promptMode === "private") {
      setSelectedPromptMode("private")
      setSelectedFiltersData(searchByOptions?.find((item) => item?.value === "Modify-Prompt"));
    }
  }, [selectedPromptMode])

  // Resize
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize); //Clean up
    };
  }, []);

  // Retrive Qustion from URL
  useEffect(() => {
    if (state?.question) {
      setQuestionInput(state?.question);
    }
  }, [state?.question]);

  useEffect(() => {
    if (state?.presetQuestion) {
      setPresetQuestion(state?.presetQuestion);
    }
  }, [state?.presetQuestion]);

  useEffect(() => {
    dispatch(FetchCompaniesReset());
    setCompanyOptions([]);
    setDropdownDataResetFlag(true);
  }, [searchType])

  // Assign Dropdown Values from API response
  useEffect(() => {
    switch (searchType) {
      case "Custom":
        const options =
          chatFolders?.foldersList?.length > 0
            ? [defaultOption, ...chatFolders?.foldersList]
            : [];
        setChatCompanyList(options);
        break;
      case "Investor":
        setChatCompanyList(companyOptions || []);
        break;
      case "Company":
        setChatCompanyList(companyOptions || []);
        break;
      case "Fund":
        setChatCompanyList(companyOptions || []);
        break;
      case "Contro":
        setChatCompanyList(companyOptions || []);
        break;
      case "RealEstate":
        setChatCompanyList(companyOptions || []);
        break;
      case "Labs":
        setChatCompanyList(companyOptions || []);
        break;
      default:
        setChatCompanyList([]);
        break;
    }
  }, [searchType, chatFolders, companyOptions, uploadFolders?.files]);

  useEffect(() => {
    if ((searchType !== "Company" || selectedPromptMode !== "private" || promptEvaluation || chunkEvaluation) && companyList?.length > 0) {
      setCompanyOptions(prevOptions => {
        if (dropdownDataResetFlag) {
          setDropdownDataResetFlag(false);
          return [...companyList];
        } else {
          const merged = [...prevOptions, ...companyList];
          const unique = Array.from(
            new Map(merged.map(item => [item.value, item])).values()
          );
          return unique;
        }
      })
    } else if (
      userPortfolioData?.entities?.length > 0 &&
      searchType === "Company" && !promptEvaluation && !chunkEvaluation
    ) {
      setCompanyOptions(userPortfolioData?.entities);
    }
  }, [companyList, selectedPromptMode, userPortfolioData, searchType, promptEvaluation, chunkEvaluation]);

  useEffect(() => {
    setShowDisclosureAlerts(disclosuresData?.alertMessage?.length > 0);
  }, [disclosuresData?.alertMessage]);

  useEffect(() => {
    if (disclosuresData?.researchOutput?.length > 0) {
      if (searchType === "Company" && !promptEvaluationFlag && !chunkEvaluation) {
        const disclosureList = filterFolders(disclosuresData?.researchOutput)
        setSourceOptions([defaultOption, ...disclosureList]);
      } else {
        setSourceOptions([defaultOption, ...disclosuresData?.researchOutput]);
      }
    } else {
      setSourceOptions([]);
    }
  }, [disclosuresData?.researchOutput]);

  useEffect(() => {
    if (countriesList?.length > 0 || userPortfolioData?.countries?.length > 0) {
      const countries = countriesList?.length > 0 ? countriesList : userPortfolioData?.countries
      setCountriesOption([defaultOption, ...(countries || [])]);
    } else {
      setCountriesOption([]);
    }
  }, [countriesList, userPortfolioData]);

  useEffect(() => {
    const userPortfolioYear = (searchType === "Company" && !promptEvaluation && !chunkEvaluation)
    const yearList = isPublicationYear ? (userPortfolioYear ? userPortfolioData?.publicationYear : publicationYearList) : (userPortfolioYear ? userPortfolioData?.fiscalYear : fiscalYearList);
    const hasDefaultYear = selectedFiltersData?.fiscalYearOption?.defaultYear;

    if (hasDefaultYear) {
      setChatFiscalYearList(yearList?.length ? [defaultOption, ...yearList] : []);
    } else {
      setChatFiscalYearList(yearList || []);
    }
  }, [isPublicationYear, fiscalYearList, publicationYearList, userPortfolioData, searchType, selectedFiltersData, promptEvaluation, chunkEvaluation]);

  useEffect(() => {
    dispatch(FetchCompaniesReset());

    if (searchType === "Custom") {
      dispatch(ESGChatFoldersAction());
    } else {
      if (selectedFiltersData?.value && selectedFiltersData?.value !== "Custom-Internal" && selectedFiltersData?.value !== "Modify-Prompt" && selectedPromptMode !== 'private') {
        const companyName = selectedFiltersData?.companyOption?.isMultiSelect
          ? selectedCompany[0]?.value === "All"
            ? []
            : selectedCompany?.map((item) => item?.value)
          : selectedCompany?.value === "All"
            ? ""
            : selectedCompany?.value;

        const payload = {
          searchType: (promptEvaluationFlag) ? selectedReseachType : selectedFiltersData?.payloadSearchType,
          searchBy: selectedFiltersData?.payloadSearchBy,
          country:
            selectedCountry === "All" ? [] : [selectedCountry],
          companyName,
          ...((searchType === "Company" && selectedReseachType !== "Investor") && { searchKeyword: searchCompany }),
        };
        if (!promptEvaluation && !chunkEvaluation && searchType === "Company" && !searchCompany) {
          dispatch(RetrievePortfolioData(payload))
        } else if (promptEvaluation || chunkEvaluation || searchType !== "Company") {
          dispatch(FetchCompaniesAction(payload));
        }
      }
    }
  }, [searchType, selectedCountry, selectedCompany, selectedFiltersData, portfolioCreateSuccess, promptEvaluation, chunkEvaluation, selectedReseachType, searchCompany]);

  useEffect(() => {
    setChatData([]);
    dispatch(ESGChatReset());
    dispatch(chunkEvaluationReset());
    dispatch(promptFiltersDataReset());

    if (selectedPromptMode !== "private") {
      setSelectedFiltersData(searchByOptions?.find((item) => item?.value === searchType));
    }
  }, [selectedPromptMode, searchType])


  useEffect(() => {
    dispatch(promptFiltersDataReset());
    if (selectedPromptMode === "private") {
      setSelectedYear(["2025"]);
      const payload = {
        searchBy: selectedFiltersData?.payloadSearchBy || "",
        searchType: selectedFiltersData?.payloadSearchType || "",
        companyName: selectedCompany?.map(item => item?.value)
      }
      dispatch(promptFiltersDataReset());
      dispatch(getPromptFiltersData(payload));
    }
  }, [selectedPromptMode, selectedCompany])

  useEffect(() => {
    if (promptFiltersDataSuccess && promptFiltersData?.companiesList?.length > 0 && selectedPromptMode === "private") {
      setCompanyOptions(promptFiltersData?.companiesList);
      setChatFiscalYearList(promptFiltersData?.fiscalYears);
      setVariableOptions(promptFiltersData?.variablesList);
    }
    if (selectedPromptMode !== "private") {
      setCompanyOptions([]);
    }
  }, [promptFiltersData, selectedPromptMode])

  useEffect(() => {
    handleLoader({ status: loadingStatus });
  }, [loadingStatus]);

  useEffect(() => {
    handleLoader({ status: chunkLoading });
  }, [chunkLoading]);

  useEffect(() => {
    handleLoader({ status: extractVariableLoading });
  }, [extractVariableLoading]);

  useEffect(() => {
    handleLoader({ status: controversyChatLoading });
  }, [controversyChatLoading]);

  // ESG Chat Response formatter
  useEffect(() => {
    if (chatResponse && questionValue) {
      setPresetQuestion("")
      const { result } = chatResponse;
      responseFormatter({
        res: result, ...promptEvaluationFlag ? {
          excelReport: {
            referenceData: chatResponse?.referenceData,
            responseData: chatResponse?.responseData,
          }
        } : {}
      });
    }
  }, [setChatData, chatResponse, setQuestionInput]);

  useEffect(() => {
    if (extractVariableData?.result && questionValue) {      
      setPresetQuestion("")
      const { result } = extractVariableData;
      responseFormatter({ res: result });
      // scrollToBottom({ scrollRef: scrollRef });
    }
  }, [extractVariableData])

  useEffect(() => {
    if (chunkData && questionValue) {
      setPresetQuestion("");
      const { retrieveChunks } = chunkData;
      responseFormatter({ res: retrieveChunks })
    }
  }, [chunkData])

  // Controversy Response formatter
  useEffect(() => {
    if (controversyChatResponse && questionValue) {
      responseFormatter({ res: controversyChatResponse?.result });
    }
  }, [controversyChatResponse]);

  // ESG Chat Error Formatter
  useEffect(() => {
    if (chatResponseError?.message || extractVariableError || chunkError) {
      const formattedChatData = {
        question: questionValue,
        answer: extractVariableError?.message ? extractVariableError?.message :
          "Failed to get response due to some Technical issue. Please try again",
      };

      const newChatData = [...chatData, formattedChatData];
      setChatData(newChatData);
      dispatch(ESGChatReset());
      dispatch(extractVariableReset());
      dispatch(chunkEvaluationReset());
      dispatch(MaxLimitAction());
    }
  }, [chatResponseError?.message, extractVariableError, chunkError]);

  // Controversy Error Formatter
  useEffect(() => {
    if (controversyChatError?.message) {
      const formattedChatData = {
        question: questionValue,
        answer:
          "Failed to get response due to some Technical issue. Please try again",
      };

      const newChatData = [...chatData, formattedChatData];
      setChatData(newChatData);
      dispatch(ESGChatControversyReset());
      dispatch(MaxLimitAction());
    }
  }, [controversyChatError?.message]);

  useEffect(() => {
    if (portfolioCreateSuccess) {
      setShowPortfolioModal(false);
      dispatch(createPortfolioReset());
    }
  }, [portfolioCreateSuccess])

  useEffect(() => {
    if (chatFeedback?.success) {
      setShowFeedback(false);
      dispatch(ESGChatFeedbackReset());
      dispatch(ESGChatReset());
      dispatch(chunkEvaluationReset());
    }
  }, [chatFeedback?.success, setShowFeedback]);

  useEffect(() => {
    // Custom Internal
    if (uploadFolders?.files) {
      const filteredFolders = filterFolders(uploadFolders.files);

      if (uploadFolders?.files?.length > 0) {
        // setChatFoldersList([defaultOption, ...filteredFolders]);
        setChatFoldersList([...filteredFolders]);
      } else {
        setChatFoldersList(filteredFolders);
      }
    }
  }, [uploadFolders?.files]);

  useEffect(() => {
    if (editPlaybookQuestionsSuccess) {
      message.success(editPlaybookQuestionsData?.message);
      dispatch(editPlaybookQuestionsReset());
      setEditPromptModal(false)
      setQuestionInput("")
      setSelectedQuestion((prev) => ({
        ...prev,
        selectedRow: {
          ...prev.selectedRow,
          criteria: null,
          fts: "",
          vts: ""
        }
      }))
      setSliderValue({ vectorWeight: "0.8", keywordWeight: "0.2" })
      dispatch(editPlaybookQuestionsReset());
    }
  }, [editPlaybookQuestionsSuccess]);

  useEffect(() => {
    dispatch(SourceListReset());
    const companyNames = selectedFiltersData?.companyOption?.isMultiSelect
      ? selectedCompany[0]?.value === "All"
        ? []
        : selectedCompany?.map((item) => item?.value)
      : selectedCompany?.value === "All"
        ? ""
        : selectedCompany?.value;

    const payloadYear = ((selectedYear === "All") || selectedYear?.length === 0) ? [] : selectedYear

    if (selectedFiltersData?.value !== "Modify-Prompt" && companyNames?.length > 0 && (!selectedFiltersData?.fiscalYearOption?.isRequired || selectedYear?.length > 0)) {
      const payload = {
        isResearch: true,
        companyName: companyNames,
        fiscalYear: isPublicationYear ? [] : payloadYear && Array.isArray(payloadYear) ? payloadYear : [payloadYear],
        publicationYear: !isPublicationYear ? [] : payloadYear && Array.isArray(payloadYear) ? payloadYear : [payloadYear],
        searchBy: selectedFiltersData?.payloadSearchBy,
        searchType: (promptEvaluationFlag) ? selectedReseachType : selectedFiltersData?.payloadSearchType,
        sourceType: [],
        subSourceType: [],
      };
      dispatch(SourceListAction(payload));
    }
  }, [selectedCompany, selectedYear, selectedReseachType]);

  useEffect(() => {
    const retrievedPayload = requestCurrentFilters();
    if (countriesOption?.length > 0 && retrievedPayload?.country) {
      setSelectedCountry(retrievedPayload?.country);
    }
  }, [countriesOption]);

  // Set Company
  useEffect(() => {
    const retrievedPayload = requestCurrentFilters();
    if (
      selectedCountry &&
      chatCompanyList?.length > 0 &&
      retrievedPayload?.companyName &&
      retrievedPayload?.companyName?.length > 0
    ) {
      setSelectedCompany(retrievedPayload?.companyName);
    }
  }, [selectedCountry]);

  // Set Fiscal Year & Range
  useEffect(() => {
    const retrievedPayload = requestCurrentFilters();
    if (selectedCompany?.length > 0 && retrievedPayload?.fiscalYear) {
      setSelectedYear(retrievedPayload?.fiscalYear);
    }
    if (selectedCompany?.length > 0 && retrievedPayload?.range) {
      setSelectedRange(retrievedPayload?.range);
    }
  }, [selectedCompany]);

  // Set Sources
  useEffect(() => {
    const retrievedPayload = requestCurrentFilters();
    if (selectedYear?.length > 0) {
      setSelectedSource(["All"])
    }
    if (
      selectedYear?.length > 0 &&
      (retrievedPayload?.sourceType || retrievedPayload?.subSourceType)
    ) {
      const sources = retrievedPayload?.sourceType || [];
      const subSources = retrievedPayload?.subSourceType || [];
      setSelectedSource([...sources, ...subSources]);
      setSelectedSourceDetails({ parents: sources, children: subSources });
      if (selectedFiltersData?.fiscalYearOption?.defaultYear) {
        setSelectedSource(["All"]);
      } else {
        setSelectedSource(["All"]);
      }
    }
  }, [selectedYear]);

  useEffect(() => {
    if (selectedFiltersData?.fiscalYearOption?.defaultYear) {
      if (selectedCompany.length > 0) {
        setSelectedYear("All");
      }
    }

  }, [selectedCompany]);

  useEffect(() => {
    if (fetchCompanyError?.message) {
      setCompanyOptions([]);
      message.error(fetchCompanyError?.message);
      dispatch(FetchCompaniesReset());
    }
  }, [fetchCompanyError?.message]);

  useEffect(() => {
    if (isPromptAdmin) {
      setSelectedModel("haiku")
    }
  }, [isPromptAdmin])

  return (
    <div className="esg-chat-container">
      {/* Right section */}
      {(showUploadSection || showDisclosuresSection) && (
        <div className="chat-right-section">
          <div className="expand-collapse-section-container">
            <div
              className="button-wrapper expand-collapse-button"
              onClick={() => handleExpandCollapse()}
            >
              {showAddonSection ? <FaChevronRight /> : <FaChevronLeft />}
            </div>
          </div>
          {showAddonSection && (
            <div className="addon-section">
              <div className="addon-section-container">
                {showUploadSection && (
                  <FileSaver
                    sectionTitle="My Files"
                    showCreateFolder
                    activeTab={"Completed"}
                    setUnsyncedFilesCount={setUnsyncedFilesCount}
                    requiredFields={["FILE"]}
                  />
                )}
                {showDisclosuresSection && (
                  <DisclosuresList
                    model={selectedFiltersData?.companyOption?.label}
                    isCompanySelected={selectedCompany?.length > 0}
                    isFYAvailable={selectedYear?.length > 0}
                    hideChildItem={searchType === "Company" && !promptEvaluationFlag}
                    promptEvaluationFlag={promptEvaluationFlag}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Left Sections */}
      <div className="contents">
        <Breadcrumbs breadcrumbDetails={breadcrumbs} />

        {/* Filters */}
        <div className="filter-section-wrapper">
          {!disableFilters && showFilters && (
            <div className="filters-section">

              {/* Prompt Mode */}
              {/* {promptEvaluationFlag && (
                <div div className="input-wrapper">
                  <div className="input-label">Prompt Mode<span>*</span></div>
                  <Select
                    showSearch
                    // allowClear
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={promptModeOptions}
                    onChange={handlePromptModeChange}
                    value={selectedPromptMode}
                    placeholder="Select Mode"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )} */}

              {/* Search By */}
              {(selectedFiltersData?.researchType?.isRequired && (promptEvaluationFlag)) && (
                <div className="input-wrapper">
                  <div className="input-label">Research Type<span>*</span></div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={researchTypeOptions}
                    onChange={handleResearchTypeChange}
                    value={selectedReseachType}
                    showSearch
                    placeholder="Select Type"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )}

              {/* Country */}
              {selectedFiltersData?.countryOption?.isRequired && (
                <div className="input-wrapper">
                  <div className="input-label">Country</div>
                  <Select
                    showSearch
                    allowClear
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={countriesOption}
                    onChange={handleCountryChange}
                    value={selectedCountry}
                    placeholder="Select Country"
                    maxLength={2}
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                    notFoundContent={countryLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : "No Data"}
                  />
                </div>
              )}

              {/* Investor / Company */}
              <div className="input-wrapper">
                {selectedFiltersData?.companyOption?.label && (
                  <div className="input-label">
                    {(promptEvaluation || chunkEvaluation) ? "Company Name" : selectedFiltersData?.companyOption?.label}
                    <span>*</span>
                  </div>
                )}

                <div className="company-select-wrapper">
                  {/* Investor / Company */}
                  {selectedFiltersData?.companyOption?.type === "select" && (
                    <Select
                      ref={selectRef}
                      className="input-main"
                      popupClassName="chat-react-select"
                      options={chatCompanyList}
                      onChange={handleCompanyChange}
                      onSearch={handleCompanySearch}
                      value={selectedCompany}
                      dropdownRender={(menu) => (
                        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                          {menu}
                          {(searchType === "Company" && !promptEvaluation && !chunkEvaluation) && <div className="add-companies" onClick={handleAddCompanies}
                            style={{ border: "1px solid #9E8B5B", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", backgroundColor: "#fafafa", color: "#9E8B5B", fontWeight: "600", fontSize: "16px", borderRadius: "5px" }}>
                            <MdSettingsSuggest /> Manage Portfolio
                          </div>}
                        </div>
                      )}
                      showSearch
                      allowClear
                      disabled={!searchType}
                      placeholder={
                        ((promptEvaluation || chunkEvaluation) && selectedReseachType === "Company" && !promptMode) ? "Type 3 or more letters to search companies" : selectedFiltersData?.companyOption?.placeholder
                      }
                      mode={
                        ((selectedFiltersData?.companyOption?.isMultiSelect || promptEvaluation) && !chunkEvaluation && !promptMode)
                          ? "multiple"
                          : undefined
                      }
                      maxTagCount={0}
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input?.toLowerCase())
                      }
                      notFoundContent={(companyLoading || promptFiltersDataLoading) ? <div className="dropdown-loading"><Spin size="small" /></div> : "No Data"}
                    />
                  )}

                  {/* Folders */}
                  {selectedFiltersData?.companyOption?.type ===
                    "tree-select" && (
                      <TreeSelect
                        className="input-main"
                        popupClassName="chat-react-select"
                        multiple
                        treeCheckable
                        treeCheckStrictly
                        showCheckedStrategy="SHOW_PARENT"
                        treeData={chatFoldersList}
                        value={selectedFolders}
                        onChange={handleFolderSelectChange}
                        placeholder={
                          selectedFiltersData?.companyOption?.placeholder
                        }
                        maxTagCount={1}
                        filterTreeNode={(input, option) => {
                          return (
                            option?.key
                              ?.toLowerCase()
                              ?.indexOf(input?.toLowerCase()) >= 0 ||
                            option?.title
                              ?.toLowerCase()
                              ?.indexOf(input?.toLowerCase()) >= 0
                          );
                        }}
                      />
                    )}

                  {(selectedFiltersData?.companyOption?.type === "select"
                    ? selectedCompany?.map((item) => item?.value)
                    : []
                  )?.length > 0 && (
                      <div
                        className="viewer-icon-container"
                        onClick={() => handleShowSelectedItems()}
                      >
                        {!showCompaniesList ? <FaEye /> : <FaEyeSlash />}
                      </div>
                    )}
                </div>
                {(userPortfolioData?.entities?.length === 0 && searchType === "Company" && !promptEvaluation && !chunkEvaluation) && (
                  <div className="portfolio-creation-alert" data-tour-id='company/research.portfolio'>
                    <div className="alert-header">
                      <FaExclamationTriangle className="alert-icon" />Alert!</div>
                    <div className="portfolio-creation-message">
                      To begin research, please add companies to your portfolio
                    </div>
                    <div className="portfolio-button-section">
                      <div className="portfolio-creation-button" onClick={handleAddCompanies}>
                        Add Companies
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Fiscal Year */}
              {selectedFiltersData?.fiscalYearOption.isRequired && (
                <div className="input-wrapper" data-tour-id='company/research.publicationYear'>
                  <div className="input-label">{isPublicationYear ? "Publication Year" : "Fiscal Year"}{selectedFiltersData?.fiscalYearOption?.defaultYear ? " " : <span>*</span>}</div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={chatFiscalYearList}
                    onChange={handleYearChange}
                    value={selectedYear}
                    mode={(searchType === "Company" && selectedCompany?.length === 1 && !promptEvaluationFlag && !chunkEvaluation) ? "multiple" : "Single"}
                    showSearch
                    allowClear
                    disabled={selectedFiltersData?.value === "Modify-Prompt" || !searchType}
                    placeholder="Select Year"
                    maxLength={2}
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                    notFoundContent={companyLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : "No Data"}
                  />
                </div>
              )}

              {/* Range */}
              {selectedFiltersData?.rangeOption.isRequired && (
                <div className="input-wrapper">
                  <div className="input-label">
                    Date Range <span>*</span>
                  </div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={rangeSelectOptions}
                    onChange={handleRangeChange}
                    value={selectedRange}
                    showSearch
                    allowClear
                    disabled={!searchType}
                    placeholder="Select Range"
                    maxLength={2}
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )}

              {/* Disclosure Type */}
              {selectedFiltersData?.sourceOption?.isRequired && (
                <div className="input-wrapper disclosure-input">
                  <div className="input-label">Disclosure Type</div>
                  <TreeSelect
                    className="input-main"
                    popupClassName="chat-react-select"
                    multiple
                    allowClear
                    showSearch
                    treeCheckable
                    maxTagCount={1}
                    value={selectedSource}
                    onChange={handleChangeSource}
                    treeData={sourceOptions}
                    showCheckedStrategy="SHOW_PARENT"
                    placeholder="Select Disclosure Type"
                    filterTreeNode={(input, option) => {
                      return (
                        option?.key
                          ?.toLowerCase()
                          ?.indexOf(input?.toLowerCase()) >= 0 ||
                        option?.title
                          ?.toLowerCase()
                          ?.indexOf(input?.toLowerCase()) >= 0
                      );
                    }}
                    notFoundContent={disclosureLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : <div className="dropdown-loading">No Data</div>}
                  />
                  {(selectedFiltersData?.sourceOption?.disclosureAlert && showDisclosureAlerts) && (
                    <div className="disclosure-alert-wrapper">
                      <div className="alert-header">
                        <FaExclamationTriangle className="alert-icon" /> Key
                        Disclosures Missing Alert!
                      </div>
                      <div
                        className="alert-message"
                        dangerouslySetInnerHTML={{
                          __html:
                            disclosuresData?.alertMessage?.replace(
                              /\n/g,
                              "<br />"
                            ) || "",
                        }}
                      />
                      <div className="alert-buttons-section">
                        <div
                          className="confirm-button"
                          onClick={() => handleCloseDisclosureAlert()}
                        >
                          Ok
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Document Language */}
              {(selectedFiltersData?.documentLanguage?.isRequired && (promptEvaluationFlag || chunkEvaluation)) && (
                <div className="input-wrapper document-language-wrapper">
                  <div className="input-label">Document Language</div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={languageOptions}
                    onChange={handleLanguageChange}
                    value={selectedLanguage}
                    showSearch
                    allowClear
                    disabled={!searchType}
                    placeholder="Select Language"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )}
              {(promptEvaluationFlag && isAdmin) && (
                <div className="input-wrapper document-language-wrapper">
                  <div className="input-label">Model</div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={modelOptions}
                    onChange={handleModelChange}
                    value={selectedModel}
                    showSearch
                    placeholder="Select Model"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )}

              {/* Child Variable */}
              {selectedFiltersData?.childVariable?.isRequired && (
                <div className="input-wrapper">
                  <div className="input-label">Variable{selectedFiltersData?.childVariable?.defaultYear ? " " : <span>*</span>}</div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={variableOptions}
                    onChange={handleVariableChange}
                    value={selectedVariable}
                    showSearch
                    allowClear
                    placeholder="Select Variable"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )}

              {/* Chunk Size */}
              {chunkEvaluation && (
                <div className="input-wrapper document-language-wrapper">
                  <div className="input-label">Chunk Size</div>
                  <Select
                    className="input-main"
                    popupClassName="chat-react-select"
                    options={chunkOptions}
                    onChange={handleChunkSizeChange}
                    value={selectedChunkSize}
                    showSearch
                    placeholder="Select Size"
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    }
                  />
                </div>
              )}

              {chunkEvaluation && (
                <div className="input-wrapper document-language-wrapper">
                  <div className="input-label">Semantic Weight</div>
                  <div className="slider">
                    <Slider min={0} max={1} step={0.05} onChange={handleVectorWeightChange} value={sliderValue?.vectorWeight} />
                    <span className="input-number-value"><InputNumber
                      min={0}
                      max={1}
                      step={0.05}
                      type="Number"
                      value={sliderValue?.vectorWeight}
                      onChange={handleVectorWeightChange}
                      formatter={(value) => {
                        if (value === null || value === undefined) return '';
                        return Number(value)?.toFixed(2)?.replace(/\.?0+$/, '');
                      }}
                      parser={(value) => parseFloat(value)}
                      className="input-number-field"
                    /></span>
                  </div>
                </div>
              )}

              {chunkEvaluation && (
                <div className="input-wrapper document-language-wrapper">
                  <div className="input-label">Keyword Weight</div>
                  <div className="slider">
                    <Slider min={0} max={1} step={0.1} onChange={handleKeywordWeightChange} value={sliderValue?.keywordWeight} disabled={true} />
                    <span className="input-number-value"><InputNumber
                      min={0}
                      max={1}
                      step={0.1}
                      value={sliderValue?.keywordWeight}
                      type="Number"
                      onChange={handleKeywordWeightChange}
                      formatter={(value) => {
                        if (value === null || value === undefined) return '';
                        return Number(value)?.toFixed(2)?.replace(/\.?0+$/, '');
                      }}
                      parser={(value) => parseFloat(value)}
                      disabled={true}
                      className="input-number-field"
                    /></span>
                  </div>
                </div>
              )}

              {showUploadSection && (
                <div className="unsync-files-section">
                  Unsynced Files :{" "}
                  <span className="file-count">{unsyncedFilesCount}</span>
                </div>
              )}
            </div>
          )}

          {/* Selected Companies */}
          {showCompaniesList && (
            <FormattedDropList
              closeDropList={handleShowSelectedItems}
              contents={
                selectedFiltersData?.companyOption?.type === "select"
                  ? selectedCompany
                  : selectedFolders
              }
              setValues={
                selectedFiltersData?.companyOption?.type === "select"
                  ? handleCompanyChange
                  : handleFolderSelectChange
              }
            />
          )}

          {/*  Show/Hide Filters */}
          <ManageFilters
            showFilters={showFilters}
            handleShowFiltersSection={handleShowFiltersSection}
          />
        </div>

        {/* Chat Section */}
        <div className="chat-container">
          <div className="chat-contents-section" ref={scrollRef}>
            <div className="chat-wrap">
              {chatData?.map((data, index) => {
                const isLastItem = index === chatData?.length - 1;
                return (
                  <ChatCard
                    key={index}
                    data={data}
                    chatIndex={index}
                    playbookQuestionFlag={chatResponse?.isPlaybookQuestion}
                    setQuestion={setQuestionInput}
                    handleFeedback={handleFeedback}
                    handlePDFPreview={handlePDFPreview}
                    setGoToPage={setCurrentPageIndex}
                    promptEvaluationFlag={promptEvaluationFlag}
                    showUploadSection={showUploadSection}
                    feedbackSuccess={chatFeedback?.success}
                    feedbackLoading={chatFeedback?.isLoading}
                    showFeedback={isLastItem && showFeedback}
                    setSelectedQuestion={setSelectedQuestion}
                    setPlaybookId={setPlaybookId}
                    selectedQuestion={selectedQuestion}
                    promptMode={selectedFiltersData?.value}
                    isControversyChat={selectedFiltersData?.value === "Contro"}
                    chunkEvaluation={chunkEvaluation}
                    setSliderValue={setSliderValue}
                  />
                );
              })}
              {showLoader && (
                <ChatLoadingCard question={questionValue} promptEvaluationFlag={promptEvaluationFlag} chunkEvaluation={chunkEvaluation} />
              )}
            </div>
          </div>

          {/* Clear Chat icon */}
          {chatData?.length > 0 && feedback && (
            <div className="clear-chat-wrapper" onClick={() => handleClearChat()} >
              <FaBroom /> Clear
            </div>
          )}
          {/* Chat Input */}
          <ChatInput
            showDragger={showInputUpload}
            promptEvaluationFlag={promptEvaluationFlag}
            handleShowEditModal={handleShowEditModal}
            handleShowUpdateModal={handleShowUpdateModal}
            chatLoading={loadingStatus || controversyChatLoading}
            handleSubmit={handleSubmit}
            questionInput={questionInput}
            setQuestionInput={setQuestionInput}
            setUploadedFiles={setUploadedFiles}
            uploadedFiles={uploadedFiles}
            handleShowPromptsModal={handleShowPromptsModal}
            showViewPlaybooks={selectedFiltersData?.viewPlaybook?.isRequired}
            searchType={searchType}
            pendingTourItems={pendingTourItemsFromStore}
            chunkEvaluation={chunkEvaluation}
            setSelectedQuestion={setSelectedQuestion}
            selectedQuestion={selectedQuestion}
          />
        </div>
      </div>

      {/* Playbook Modal */}
      {showPlaybookModal && (
        <CustomModal
          handleClose={handleShowPromptsModal}
          showCloseIcon
          modalTitle="ESG Playbook"
        >
          <PlaybookMain
            isModalContent
            setQuestionInput={setQuestionInput}
            setPresetQuestion={setPresetQuestion}
            handleShowPromptsModal={handleShowPromptsModal}
            promptEvaluation={promptEvaluationFlag}
            chunkEvaluation={chunkEvaluation}
          />
        </CustomModal>
      )}

      {/* Custom Playbook Modal */}
      {(showPlaybookModal && (promptEvaluationFlag || chunkEvaluation)) && (
        <CustomModal
          handleClose={handleShowPromptsModal}
          showCloseIcon
          modalTitle="ESG Playbook"
        >
          <CustomPlaybookMain
            isModalContent
            setQuestionInput={setQuestionInput}
            setSliderValue={setSliderValue}
            handleShowPromptsModal={handleShowPromptsModal}
            setSelectedQuestion={setSelectedQuestion}
            promptEvaluationFlag={promptEvaluationFlag}
            playbookId={playbookId}
            setPlaybookId={setPlaybookId}
            chunkEvaluation={chunkEvaluation}
          // setEditPromptModal={setEditPromptModal}
          />
        </CustomModal>
      )}
      {/* Edit Prompt Modal */}
      {(editPromptModal && (promptEvaluationFlag || chunkEvaluation)) && (
        <CustomModal
          handleClose={handleShowEditModal}
          showCloseIcon
          modalTitle={`${selectedQuestion?.selectedRow?.playbookName || ""}/${selectedQuestion?.selectedRow?.dpCode ?? ""}`}
        >
          <EditModal
            selectedObject={selectedQuestion?.selectedRow}
            handleShowEditModal={handleShowEditModal}
            handleClose={handleShowEditModal || handleShowUpdateModal}
            showEditModal={editPromptModal}
            dispatch={dispatch}
            playbookId={selectedQuestion?.selectedRow?.playbookId}
            editPlaybookQuestionsLoading={editPlaybookQuestionsLoading}
            additionalNote={true}
            additionalFields={false}
            editModal={editModal}
            setEditPromptModal={setEditPromptModal}
            setQuestionInput={setQuestionInput}
            setSelectedQuestion={setSelectedQuestion}
            promptEvaluationFlag={promptEvaluationFlag}
            chunkEvaluation={chunkEvaluation}
            dataTypeList={dataTypeList}
            setSliderValue={setSliderValue}
            additionalNoteContent={
              <>
                Note*: This change will be reflected in the <strong>{selectedQuestion?.selectedRow?.playbookName}</strong> playbook
              </>
            }
          />
        </CustomModal>
      )}

      {showPortfolioModal && (
        <CustomModal
          showCloseIcon
          handleClose={() => setShowPortfolioModal(false)}
          modalTitle="Manage Portfolio"
        >
          <Portfolio showBreadCrumbs={false} />
        </CustomModal>
      )}

      {/* PDF Preview */}
      {showPDFPreview?.show && (
        <PDFPreview
          setShow={setShowPDFPreview}
          isDocDownload={!showPDFPreview?.isPdf}
          goToPage={currentPageIndex}
          setGoToPage={setCurrentPageIndex}
        />
      )}
    </div>
  );
};

export default ESGChatMain;
